import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAppSettings, useClientBrand } from "../context/AppSettingsContext";
import { PublicShell } from "./PublicHome";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  ChevronLeft, ArrowRight, ShieldCheck, CreditCard, User, QrCode, X as XIcon,
  Lock, KeyRound, Eye, EyeOff, Mail, MessageCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Customer login. Identification methods (Card / Cognome / QR) resolve the
 * card_number; the user then enters the password chosen at registration.
 * OTP is used only for password recovery.
 */
export default function ClientLogin() {
  const nav = useNavigate();
  const { settings } = useAppSettings();

  // Login buttons area cliente forzati BLU+BIANCO come da brand richiesto
  const { brand } = useClientBrand();

  const [mode, setMode] = useState("card"); // card | surname | qr
  const [step, setStep] = useState("input"); // input | password | reset-choice | reset-sent
  const [card, setCard] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);

  // Surname flow
  const [surname, setSurname] = useState("");
  const [phone4, setPhone4] = useState("");
  const [matches, setMatches] = useState([]);

  // QR scanner
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const scannerInstance = useRef(null);

  // Password reset via magic link
  const [resetInfo, setResetInfo] = useState(null); // {channel, phone_masked?, email_masked?, wa_link?}

  const goPasswordFor = (cardValue) => {
    setCard(cardValue.trim());
    setStep("password");
    setPassword("");
  };

  const goCardMode = () => {
    if (!card.trim()) return toast.error("Inserisci il numero della card");
    goPasswordFor(card);
  };

  const doLogin = async () => {
    if (!password) return toast.error("Inserisci la password");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/password/login`, {
        card_number: card.trim(), password,
      });
      try {
        localStorage.setItem(`otp_session_${card.trim()}`, JSON.stringify({
          session_token: data.session_token, card_number: card.trim(),
          expires_at: data.expires_at,
        }));
      } catch {}
      nav(`/carta/${encodeURIComponent(card.trim())}${new URLSearchParams(window.location.search).get("next") === "cart" ? "?cart=1" : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenziali non valide");
    } finally { setBusy(false); }
  };

  const doSurnameLookup = async () => {
    const s = surname.trim();
    if (s.length < 2) return toast.error("Inserisci almeno 2 caratteri del cognome");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/lookup`, {
        surname: s, phone_last4: phone4.trim() || null,
      });
      if (data.matches.length === 1) {
        goPasswordFor(data.matches[0].card_number);
        setMatches([]);
      } else {
        setMatches(data.matches);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ricerca fallita");
    } finally { setBusy(false); }
  };

  // -------------- Password recovery via magic link --------------
  const requestReset = async (channel) => {
    if (!card.trim()) return toast.error("Manca la card");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/password/reset/request`, {
        card_number: card.trim(),
        channel,
      });
      setResetInfo(data);
      setStep("reset-sent");
      toast.success(channel === "email" ? "Email inviata" : "Messaggio WhatsApp inviato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invio fallito");
    } finally { setBusy(false); }
  };

  // -------------- QR scanning --------------
  const startQr = async () => {
    setScanning(true);
    const { Html5Qrcode } = await import("html5-qrcode");
    await new Promise((r) => setTimeout(r, 50));
    if (!scannerRef.current) return;
    const inst = new Html5Qrcode(scannerRef.current.id);
    scannerInstance.current = inst;
    try {
      await inst.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decoded) => {
          await stopQr();
          const cardVal = String(decoded || "").trim();
          if (!cardVal) return toast.error("QR non valido");
          goPasswordFor(cardVal);
        },
        () => { /* ignore decode errors */ },
      );
    } catch (err) {
      toast.error("Impossibile aprire la fotocamera. Autorizza l'accesso e riprova.");
      setScanning(false);
    }
  };

  const stopQr = async () => {
    setScanning(false);
    const inst = scannerInstance.current;
    if (!inst) return;
    try { await inst.stop(); await inst.clear(); } catch {}
    scannerInstance.current = null;
  };

  useEffect(() => () => { stopQr(); }, []);
  useEffect(() => { if (mode !== "qr") stopQr(); }, [mode]);

  const modes = [
    { k: "card",    l: "Card",    icon: CreditCard },
    { k: "surname", l: "Cognome", icon: User },
    { k: "qr",      l: "QR",      icon: QrCode },
  ];

  return (
    <PublicShell title={step === "input" ? "Accedi" : step === "password" ? "Password" : "Recupera password"} back="/cliente" testid="client-login">
      <div className="light-auth -mx-4">
      <main className="max-w-md mx-auto px-6 py-2 space-y-5">
        <div className="text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl flex items-center justify-center mb-4"
               style={{ background: `${brand}18`, color: brand }}>
            {step === "reset-otp" || step === "reset-pass" ? <KeyRound className="h-7 w-7" /> : <ShieldCheck className="h-7 w-7" />}
          </div>
          <h1 className="font-heading font-extrabold text-2xl">
            {step === "password" && "Inserisci la password"}
            {step === "reset-choice" && "Recupera password"}
            {step === "reset-sent" && "Link inviato"}
            {step === "input" && "Accedi alla tua Area Riservata"}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {step === "password" && `Card ${card}`}
            {step === "reset-choice" && "Come vuoi ricevere il link per crearne una nuova?"}
            {step === "reset-sent" && (resetInfo?.channel === "email"
              ? `Abbiamo inviato il link a ${resetInfo?.email_masked || "la tua email"}.`
              : `Ti abbiamo inviato il link su WhatsApp al numero ${resetInfo?.phone_masked || "registrato"}.`)}
            {step === "input" && "Card, cognome o scansione QR"}
          </p>
        </div>

        {/* ---- STEP: identification ---- */}
        {step === "input" && (
          <>
            <div className="grid grid-cols-3 gap-2 bg-zinc-100 p-1 rounded-xl" data-testid="client-login-mode-tabs">
              {modes.map((m) => {
                const on = mode === m.k;
                return (
                  <button
                    key={m.k}
                    data-testid={`client-login-mode-${m.k}`}
                    onClick={() => { setMode(m.k); setMatches([]); }}
                    className={`py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 transition-all ${
                      on ? "shadow-sm text-white" : "text-zinc-500 hover:text-zinc-800"
                    }`}
                    style={on ? { background: brand } : {}}
                  >
                    <m.icon className="h-4 w-4" /> {m.l}
                  </button>
                );
              })}
            </div>

            {mode === "card" && (
              <div className="space-y-3">
                <Label className="text-zinc-700 text-sm">Numero card</Label>
                <Input
                  data-testid="client-login-card"
                  placeholder="es. K0001"
                  value={card}
                  onChange={(e) => setCard(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && goCardMode()}
                  className="h-12 bg-white border-zinc-300 text-lg font-mono"
                  autoFocus
                />
                <Button
                  data-testid="client-login-next"
                  onClick={goCardMode}
                  disabled={busy}
                  className="w-full h-12 text-white font-semibold text-base"
                  style={{ background: brand }}
                >
                  Prosegui <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}

            {mode === "surname" && (
              <div className="space-y-3" data-testid="client-login-surname-form">
                <div>
                  <Label className="text-zinc-700 text-sm">Cognome</Label>
                  <Input
                    data-testid="client-login-surname"
                    placeholder="es. Rossi"
                    value={surname}
                    onChange={(e) => setSurname(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSurnameLookup()}
                    className="h-12 bg-white border-zinc-300 text-lg"
                    autoFocus
                  />
                </div>
                <div>
                  <Label className="text-zinc-700 text-sm">Ultime 4 cifre del telefono <span className="text-zinc-400">(consigliato)</span></Label>
                  <Input
                    data-testid="client-login-phone4"
                    placeholder="es. 1234"
                    inputMode="numeric"
                    maxLength={4}
                    value={phone4}
                    onChange={(e) => setPhone4(e.target.value.replace(/\D/g, ""))}
                    className="h-12 bg-white border-zinc-300 text-lg font-mono tracking-widest"
                  />
                </div>
                <Button
                  data-testid="client-login-surname-search"
                  onClick={doSurnameLookup}
                  disabled={busy || surname.trim().length < 2}
                  className="w-full h-12 text-white font-semibold text-base"
                  style={{ background: brand }}
                >
                  {busy ? "Ricerca…" : "Trova la mia card"} <ArrowRight className="h-4 w-4 ml-1" />
                </Button>

                {matches.length > 0 && (
                  <div className="space-y-2 pt-2" data-testid="client-login-matches">
                    <div className="text-xs text-zinc-500 uppercase tracking-widest">Seleziona la tua card</div>
                    {matches.map((m) => (
                      <button
                        key={m.card_number}
                        data-testid={`client-login-match-${m.card_number}`}
                        onClick={() => goPasswordFor(m.card_number)}
                        disabled={busy}
                        className="w-full flex items-center justify-between p-3 rounded-xl border-2 hover:bg-red-50 transition-colors text-left"
                        style={{ borderColor: `${brand}30` }}
                      >
                        <div>
                          <div className="font-semibold text-zinc-900">{m.name_masked}</div>
                          <div className="text-xs text-zinc-500">Tel {m.phone_masked}</div>
                        </div>
                        <ArrowRight className="h-4 w-4" style={{ color: brand }} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {mode === "qr" && (
              <div className="space-y-3" data-testid="client-login-qr-form">
                <p className="text-sm text-zinc-500 text-center">
                  Inquadra il QR presente sulla tua card o nel Wallet.
                </p>
                {!scanning ? (
                  <Button
                    data-testid="client-login-qr-start"
                    onClick={startQr}
                    disabled={busy}
                    className="w-full h-12 text-white font-semibold text-base"
                    style={{ background: brand }}
                  >
                    <QrCode className="h-4 w-4 mr-1" /> Avvia scansione
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <div
                      id="client-login-qr-region"
                      ref={scannerRef}
                      data-testid="client-login-qr-region"
                      className="rounded-2xl overflow-hidden bg-black aspect-square"
                    />
                    <Button
                      data-testid="client-login-qr-stop"
                      onClick={stopQr}
                      variant="outline"
                      className="w-full"
                    >
                      <XIcon className="h-4 w-4 mr-1" /> Annulla scansione
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ---- STEP: password ---- */}
        {step === "password" && (
          <div className="space-y-3" data-testid="client-login-password-step">
            <Label className="text-zinc-700 text-sm">Password</Label>
            <div className="relative">
              <Lock className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="client-login-password"
                type={showPass ? "text" : "password"}
                placeholder="La tua password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doLogin()}
                className="h-12 bg-white border-zinc-300 text-lg pl-9 pr-9"
                autoFocus
              />
              <button
                type="button"
                data-testid="client-login-password-toggle"
                onClick={() => setShowPass((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                aria-label={showPass ? "Nascondi password" : "Mostra password"}
              >
                {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Button
              data-testid="client-login-submit"
              onClick={doLogin}
              disabled={busy}
              className="w-full h-12 text-white font-semibold text-base"
              style={{ background: brand }}
            >
              {busy ? "Accesso…" : "Accedi"} <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <div className="flex items-center justify-between text-xs pt-1">
              <button
                data-testid="client-login-change-method"
                onClick={() => { setStep("input"); setPassword(""); }}
                className="text-zinc-500 hover:text-zinc-900"
              >
                ← Cambia metodo
              </button>
              <button
                data-testid="client-login-forgot"
                onClick={() => setStep("reset-choice")}
                disabled={busy}
                className="hover:underline font-semibold"
                style={{ color: brand }}
              >
                Password dimenticata?
              </button>
            </div>
          </div>
        )}

        {/* ---- STEP: reset choice (email or whatsapp) ---- */}
        {step === "reset-choice" && (
          <div className="space-y-3" data-testid="client-login-reset-choice">
            <p className="text-xs text-zinc-500 -mt-2">
              Ti manderemo un link temporaneo (valido 30 min) sul canale che scegli.
            </p>
            <button
              data-testid="client-login-reset-email"
              onClick={() => requestReset("email")}
              disabled={busy}
              className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 hover:bg-red-50 active:scale-[0.98] transition text-left"
              style={{ borderColor: brand, color: brand }}
            >
              <span className="h-11 w-11 rounded-xl flex items-center justify-center"
                    style={{ background: `${brand}18` }}>
                <Mail className="h-5 w-5" />
              </span>
              <span className="flex-1">
                <span className="block font-bold">Via Email</span>
                <span className="block text-xs text-zinc-500">All'indirizzo registrato</span>
              </span>
              <ArrowRight className="h-5 w-5" />
            </button>
            <button
              data-testid="client-login-reset-whatsapp"
              onClick={() => requestReset("whatsapp")}
              disabled={busy}
              className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-[#25D366] hover:bg-emerald-50 active:scale-[0.98] transition text-left text-[#128C7E]"
            >
              <span className="h-11 w-11 rounded-xl flex items-center justify-center bg-[#25D366]/15">
                <MessageCircle className="h-5 w-5" />
              </span>
              <span className="flex-1">
                <span className="block font-bold">Via WhatsApp</span>
                <span className="block text-xs text-zinc-500">Sul numero registrato</span>
              </span>
              <ArrowRight className="h-5 w-5" />
            </button>
            <button
              onClick={() => setStep("password")}
              className="w-full text-center text-xs text-zinc-500 hover:text-zinc-900 pt-2"
            >
              ← Torna all'accesso
            </button>
          </div>
        )}

        {/* ---- STEP: reset link sent confirmation ---- */}
        {step === "reset-sent" && (
          <div className="space-y-3 text-center" data-testid="client-login-reset-sent">
            <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
              {resetInfo?.channel === "email" ? (
                <>Controlla la casella <strong>{resetInfo?.email_masked || "email"}</strong> e clicca il link per creare la nuova password.</>
              ) : (
                <>Apri WhatsApp: hai ricevuto un messaggio con il link per creare la nuova password (valido 30 minuti).</>
              )}
            </div>
            <button
              onClick={() => setStep("password")}
              className="w-full text-center text-xs text-zinc-500 hover:text-zinc-900"
            >
              ← Torna all'accesso
            </button>
          </div>
        )}

        {step === "input" && (
          <div className="text-center text-sm text-zinc-500 pt-4">
            Non hai una card?{" "}
            <Link to="/registrati" className="font-semibold hover:underline" style={{ color: brand }} data-testid="client-login-goto-register">
              Registrati
            </Link>
          </div>
        )}
        {settings.support_email && (
          <div className="text-center text-xs text-zinc-400" data-testid="client-login-support">
            Problemi di accesso? Scrivi a{" "}
            <a href={`mailto:${settings.support_email}`} className="underline hover:text-zinc-700" data-testid="client-login-support-email">{settings.support_email}</a>
          </div>
        )}
      </main>
      </div>
    </PublicShell>
  );
}
